import { clearStoredAuthToken, getStoredAuthToken, setStoredAuthToken } from './auth-token'
import type { FollowUpBucket, FollowUpTask } from './types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000'

interface ApiError {
    message?: string | string[]
    statusCode?: number
    error?: string
    details?: string
    errors?: Array<{ field?: string; message?: string }>
    fieldErrors?: Record<string, string[]>
}

export class ApiRequestError extends Error {
    statusCode: number
    fieldErrors: Record<string, string[]>

    constructor(message: string, statusCode: number, fieldErrors: Record<string, string[]> = {}) {
        super(message)
        this.name = 'ApiRequestError'
        this.statusCode = statusCode
        this.fieldErrors = fieldErrors
    }
}

export interface AuthenticatedResult {
    status: 'authenticated'
    token?: string
    csrfToken?: string
    user: {
        id: string
        email: string
        name: string
        tenantId: string
        role: string
        locale?: string
    }
    tenant?: {
        id: string
        name: string
        slug: string
        planId: string
    }
}

export interface TwoFactorRequiredResult {
    status: 'two_factor_required'
    challengeToken: string
}

export interface TwoFactorSetupRequiredResult {
    status: 'two_factor_setup_required'
    setupToken: string
    tenant?: { name: string }
}

export type LoginResult =
    | AuthenticatedResult
    | TwoFactorRequiredResult
    | TwoFactorSetupRequiredResult

export interface Membership {
    tenantId: string
    tenantName: string
    tenantSlug: string
    planId: string
    logoImageUrl: string | null
    role: string
    seatStatus: string
    isActive: boolean
}

export type ProfilePublicationStatus =
    | 'draft'
    | 'pending_consent'
    | 'published'
    | 'declined'
    | 'revoked'

export interface ProfessionalProfileSummary {
    id: string
    slug: string
    displayName: string
    photoUrl: string | null
    specialty: string | null
    bio: string | null
    socialLinks: Record<string, string> | null
    socialLinksConsent: boolean
    publicationStatus: ProfilePublicationStatus
    consentRequestedAt: string | null
    consentAcceptedAt: string | null
    consentDeclinedAt: string | null
    consentRevokedAt: string | null
    contentVersion: number
    publishedContentVersion: number | null
    lastMaterialEditAt: string | null
    sortOrder: number
    isFeatured: boolean
}

export interface StudioProfessionalListItem {
    id: string
    userId: string
    active: boolean
    user: { name: string; email: string }
    profile: ProfessionalProfileSummary | null
    publicationStatus: ProfilePublicationStatus | null
    assignmentCount: number
}

export interface ProfessionalPortfolioItem {
    id: string
    imageUrl: string
    caption: string | null
    sortOrder: number
}

export interface MyServiceAssignment {
    id: string
    packageId: string
    packageTitle: string
    packageActive: boolean
    currency: string
    priceCents: number
    durationMinutes: number | null
    pricingSource: 'package_base' | 'professional_override'
}

export interface MyProfessionalRecord {
    id: string
    userId: string
    active: boolean
    profile: ProfessionalProfileSummary | null
    portfolioItems: ProfessionalPortfolioItem[]
    assignments: MyServiceAssignment[]
}

export type PermissionKey =
    | 'canCreatePackages'
    | 'canEditOrgPackages'
    | 'canCreateOwnPackages'
    | 'canPublishToOrg'
    | 'canViewPricing'
    | 'canEditPricing'
    | 'canApplyDiscount'
    | 'canViewOrgReports'
    | 'canViewOwnReports'
    | 'canExportData'
    | 'canManageClients'
    | 'canManageOwnClients'
    | 'canManagePolicies'
    | 'canManageTemplates'
    | 'canCreateProposals'
    | 'canCreateOnBehalf'
    | 'canManageBilling'
    | 'canManageTeam'
    | 'canViewAuditLog'
    | 'canManageSecurity'
    | 'canViewAllProposals'
    | 'canManageAllProposals'
    | 'canManageOwnProposals'
    | 'canViewAllClients'
    | 'canManageCustomerPayments'
    | 'canIssueRefunds'
    | 'canWaiveCharges'
    | 'canManageSubscriptionBilling'
    | 'canManageSeats'
    | 'canManageRoles'

export interface MyPermissionsResponse {
    role: string
    seatStatus: string
    permissions: Record<PermissionKey, boolean>
}

export interface ProfessionalDashboardSummary {
    hasProfessionalRecord: boolean
    stats: {
        upcomingAssignedCount: number
        pendingProposals: number
        sentProposals: number
        viewedProposals: number
        assignmentsCount: number
    }
    upcomingEvents: Array<{
        id: string
        date: string
        state: 'soft' | 'hard'
        title: string
    }>
    profile: {
        displayName: string | null
        publicationStatus: string | null
    } | null
}

export interface UpsertProfessionalProfileInput {
    displayName?: string
    photoUrl?: string | null
    photoPublicId?: string | null
    specialty?: string | null
    bio?: string | null
    socialLinks?: Record<string, string> | null
    socialLinksConsent?: boolean
    sortOrder?: number
    isFeatured?: boolean
}

export interface PackageServiceAssignment {
    id: string
    tenantId: string
    packageId: string
    studioProfessionalId: string
    priceCentsOverride?: number | null
    durationMinutesOverride?: number | null
    active: boolean
}

export interface ServiceAssignmentInput {
    studioProfessionalId: string
    priceCentsOverride?: number | null
    durationMinutesOverride?: number | null
    active?: boolean
}

export interface AvailabilityWindow {
    id: string
    weekday: number
    startMinutes: number
    endMinutes: number
    label: string | null
    active: boolean
}

export interface AvailabilityWindowInput {
    weekday: number
    startMinutes: number
    endMinutes: number
    label?: string | null
    active?: boolean
}

export interface AvailabilityException {
    id: string
    date: string
    startMinutes: number | null
    endMinutes: number | null
    isAllDay: boolean
    reason: string | null
    active: boolean
}

export interface AvailabilityExceptionInput {
    date: string
    startMinutes?: number | null
    endMinutes?: number | null
    isAllDay?: boolean
    reason?: string | null
}

export interface ProfessionalAvailability {
    windows: AvailabilityWindow[]
    exceptions: AvailabilityException[]
}

export interface SeatBillingSummary {
    planSource: 'stripe' | 'admin' | 'none'
    teamFeatures: boolean
    canManageBilling: boolean
    seats: {
        included: number
        extra: number
        scheduledExtra: number | null
        occupied: number
        available: number
        maxRemovable: number
        total: number
    }
    billing: {
        interval: 'month' | 'year' | null
        currency: 'BRL' | 'USD' | 'EUR'
        unitAmountCents: number
        nextRenewalAt: string | null
        estimatedNextInvoiceCents: number | null
        status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing' | 'none'
        hasPaymentMethod: boolean
        scheduledRemoval: { effectiveAt: string; newExtraQuantity: number } | null
    }
}

export interface SeatChangePreview {
    currentExtra: number
    newExtra: number
    interval: 'month' | 'year'
    currency: 'BRL'
    unitAmountCents: number
    prorationAmountCents: number
    newRecurringAmountCents: number
    nextRenewalAt: string | null
}

export type DomainSeatEventType =
    | 'seats_added'
    | 'seats_removal_scheduled'
    | 'seats_removal_canceled'
    | 'seats_removal_applied'
    | 'proration_charged'
    | 'payment_failed'
    | 'subscription_canceled'
    | 'renewed'

export interface DomainSeatEvent {
    id: string
    type: DomainSeatEventType
    quantity?: number
    effectiveAt?: string
    amountCents?: number
    currency?: string
    actorName?: string
    createdAt: string
}

export class ApiClient {
    private baseURL: string
    private csrfToken: string | null = null
    private authToken: string | null = null

    constructor(baseURL: string = API_BASE_URL) {
        this.baseURL = baseURL
        this.csrfToken = this.readCsrfTokenFromCookies()
        this.authToken = getStoredAuthToken()
    }

    private readCsrfTokenFromCookies(): string | null {
        if (typeof document === 'undefined') {
            return null
        }

        const csrfPair = document.cookie
            .split(';')
            .map((part) => part.trim())
            .find((part) => part.startsWith('csrf_token='))

        if (!csrfPair) {
            return null
        }

        return decodeURIComponent(csrfPair.substring('csrf_token='.length))
    }

    refreshCsrfToken() {
        this.csrfToken = this.readCsrfTokenFromCookies()
    }

    setAuthToken(token: string | null | undefined) {
        const normalized = token?.trim()
        this.authToken = normalized || null
        setStoredAuthToken(this.authToken)
    }

    clearAuthToken() {
        this.authToken = null
        clearStoredAuthToken()
    }

    private getAuthToken() {
        if (this.authToken) {
            return this.authToken
        }

        this.authToken = getStoredAuthToken()
        return this.authToken
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        }

        const method = (options.method ?? 'GET').toUpperCase()
        const authToken = this.getAuthToken()
        if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`
        }

        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
            this.refreshCsrfToken()
            if (this.csrfToken) {
                headers['x-csrf-token'] = this.csrfToken
            }
        }

        const response = await fetch(`${this.baseURL}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include',
        })

        if (!response.ok) {
            if (response.status === 401 && endpoint !== '/api/auth/login') {
                this.clearAuthToken()
            }

            const error: ApiError = await response.json().catch(() => ({
                message: 'Unknown error',
                statusCode: response.status,
            }))

            const normalizedMessage = Array.isArray(error.message)
                ? error.message.join(', ')
                : error.message

            const fallbackFieldErrors = (error.errors || []).reduce<Record<string, string[]>>((accumulator, fieldError) => {
                if (!fieldError?.field || !fieldError?.message) {
                    return accumulator
                }

                if (!accumulator[fieldError.field]) {
                    accumulator[fieldError.field] = []
                }

                accumulator[fieldError.field].push(fieldError.message)
                return accumulator
            }, {})

            const mergedFieldErrors = {
                ...fallbackFieldErrors,
                ...(error.fieldErrors || {}),
            }

            const firstFieldError = Object.values(mergedFieldErrors)[0]?.[0]
                || error.errors?.find((fieldError) => fieldError?.message)?.message
            const resolvedMessage = firstFieldError
                || error.details
                || normalizedMessage
                || error.error
                || `API Error (${response.status})`

            throw new ApiRequestError(resolvedMessage, response.status, mergedFieldErrors)
        }

        return response.json()
    }

    // Auth
    async login(email: string, password: string): Promise<LoginResult> {
        const result = await this.request<LoginResult>('/api/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        })

        // Only a fully-authenticated response carries a definitive session. When a
        // second factor (or forced enrollment) is required, NO token is set here —
        // the caller must complete the 2FA step first.
        if (result.status === 'authenticated') {
            this.setAuthToken(result.token)
            this.refreshCsrfToken()
        }
        return result
    }

    /** Complete a two-step login (or tenant-switch step-up) with a TOTP / recovery code. */
    async verifyTwoFactor(challengeToken: string, code: string): Promise<AuthenticatedResult> {
        const result = await this.request<AuthenticatedResult>('/api/auth/2fa/verify', {
            method: 'POST',
            body: JSON.stringify({ challengeToken, code }),
        })
        this.setAuthToken(result.token)
        this.refreshCsrfToken()
        return result
    }

    async startTwoFactorSetup(setupToken?: string) {
        return this.request<{ qrCode: string; manualKey: string }>('/api/auth/2fa/setup', {
            method: 'POST',
            body: JSON.stringify(setupToken ? { setupToken } : {}),
        })
    }

    /**
     * Confirm a pending enrollment. During forced enrollment (setupToken present)
     * the response also carries a full authenticated session.
     */
    async confirmTwoFactorSetup(code: string, setupToken?: string) {
        const result = await this.request<
            { recoveryCodes: string[] } & Partial<AuthenticatedResult>
        >('/api/auth/2fa/setup/confirm', {
            method: 'POST',
            body: JSON.stringify(setupToken ? { code, setupToken } : { code }),
        })
        if (result.status === 'authenticated' && result.token) {
            this.setAuthToken(result.token)
            this.refreshCsrfToken()
        }
        return result
    }

    async getOrganizationInfo() {
        return this.request<{
            id: string
            name: string
            slug: string
            logoImageUrl: string | null
            currency: string
            timeZone: string
            planId: string
            require2FA: boolean
            membership: {
                role: string
                seatStatus: string
                joinedAt: string | null
            }
        }>('/api/organization/me')
    }

    async getTwoFactorStatus() {
        return this.request<{
            enabled: boolean
            confirmedAt: string | null
            recoveryCodesRemaining: number
            requiredByActiveTenant: boolean
        }>('/api/auth/2fa/status')
    }

    async disableTwoFactor(password: string, code: string) {
        return this.request<{ ok: true }>('/api/auth/2fa/disable', {
            method: 'POST',
            body: JSON.stringify({ password, code }),
        })
    }

    async regenerateRecoveryCodes(password: string, code: string) {
        return this.request<{ recoveryCodes: string[] }>('/api/auth/2fa/recovery-codes/regenerate', {
            method: 'POST',
            body: JSON.stringify({ password, code }),
        })
    }

    async register(data: {
        email: string
        password: string
        name: string
        businessName: string
        locale?: string
        currency?: string
        inviteCode?: string
    }) {
        const result = await this.request<{
            status: 'verification_required'
            email: string
            user?: {
                id: string
                email: string
                name: string
            }
            emailSent: boolean
            message: string
            betaInvite?: {
                planGranted: string
                expiresAt: string
            }
        }>('/api/auth/register', {
            method: 'POST',
            body: JSON.stringify(data),
        })

        this.clearAuthToken()
        return result
    }

    async validateToken() {
        return this.request<{
            id: string
            email: string
            name: string
            tenantId: string
            role: string
            planId?: string
            billingProvider?: 'stripe' | 'hotmart' | 'none'
        }>('/api/users/me')
    }

    async logout() {
        try {
            return await this.request<{ ok: true }>('/api/auth/logout', {
                method: 'POST',
            })
        } catch (error) {
            if (error instanceof ApiRequestError && error.statusCode === 404) {
                return { ok: true }
            }

            throw error
        } finally {
            this.clearAuthToken()
            this.refreshCsrfToken()
        }
    }

    async requestPasswordReset(email: string) {
        return this.request<{ ok: true }>('/api/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        })
    }

    async resetPassword(token: string, password: string) {
        return this.request<{ ok: true }>('/api/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, password }),
        })
    }

    // Stats endpoints for dashboard
    async getDashboardSummary(range: string = '7d') {
        return this.request<{
            depositsAmount: number
            depositsCount: number
            conversion48h: number
            upcomingHolds: number
            pendingProposals: number
            pendingBalances: number
            sentProposals: number
            viewedProposals: number
        }>(`/api/stats/summary?range=${range}`)
    }

    async getMyDashboardSummary(range: string = '7d') {
        return this.request<ProfessionalDashboardSummary>(`/api/stats/my-summary?range=${range}`)
    }

    async getMyPermissions() {
        return this.request<MyPermissionsResponse>('/api/seats/permissions/me')
    }

    async getAttentionItems() {
        return this.request<Array<{
            type: 'proposal-expiring' | 'pix-pending' | 'pix-awaiting-confirmation' | 'balance-due'
            id: string
            title?: string
            client?: string
            proposalId?: string
            amount?: number
            currency?: string
            pixCopyPaste?: string
            expiresInHours?: number
            ageMinutes?: number
            dueInDays?: number
        }>>('/api/attention')
    }

    async getUpcomingEvents(days: number = 14) {
        return this.request<Array<{
            id: string
            date: string
            state: 'soft' | 'hard'
            title: string
        }>>(`/api/events/upcoming?days=${days}`)
    }

    async getUsage() {
        return this.request<{
            used: number
            limit: number
            plan?: {
                id?: string
                maxEventsPerMonth?: number
                maxProposalsPerMonth?: number
            }
            usage?: {
                eventsCount?: number
                proposalsCount?: number
            }
        }>('/api/usage')
    }

    async getPackages() {
        return this.request<Array<{
            id: string
            title: string
            active: boolean
        }>>('/api/packages')
    }

    async getTenantSettings() {
        return this.request<{
            id: string
            name: string
            slug: string
            planId: string
            currency: string
            timeZone: string
            depositPercentage: number
            softHoldDurationHours: number
            cancellationWindowDays: number
            rescheduleFeePercentage: number
            defaultCancellationPolicyId: string | null
        }>('/api/tenants/settings')
    }

    async getActivityFeed(limit: number = 20) {
        return this.request<Array<{
            id: string
            ts: string
            text: string
            href?: string
            type?: 'success' | 'error' | 'info' | 'payment'
        }>>(`/api/activity?limit=${limit}`)
    }

    // Follow-ups
    async getFollowUps(bucket: FollowUpBucket = 'due_today') {
        return this.request<FollowUpTask[]>(`/api/follow-ups?bucket=${encodeURIComponent(bucket)}`)
    }

    async markFollowUpSent(id: string) {
        return this.request<FollowUpTask>(`/api/follow-ups/${id}/mark-sent`, {
            method: 'POST',
        })
    }

    async snoozeFollowUp(id: string, snoozeUntil: string) {
        return this.request<FollowUpTask>(`/api/follow-ups/${id}/snooze`, {
            method: 'POST',
            body: JSON.stringify({ snoozeUntil }),
        })
    }

    async skipFollowUp(id: string) {
        return this.request<FollowUpTask>(`/api/follow-ups/${id}/skip`, {
            method: 'POST',
        })
    }

    async markProposalSent(id: string) {
        return this.request<FollowUpTask | null>(`/api/proposals/${id}/mark-sent`, {
            method: 'POST',
        })
    }

    // Proposals
    async createProposal(data: {
        clientName: string
        clientEmail: string
        clientPhone?: string
        eventDate: string
        location?: string
        packageId?: string
        billingMode?: 'one_off' | 'recurring'
        depositPct: number
        language?: string
        expiresAt?: string
        sendImmediately?: boolean
        sendWhatsApp?: boolean
    }) {
        return this.request<{
            kind: 'proposal_created'
            proposal: unknown
            publicShareUrl: string
        } | {
            kind: 'waitlist_created'
            waitlistEntryId: string
            dateKey: string
            position: number
            conflictingProposalId: string
            message: string
        }>('/api/proposals', {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async cancelProposal(id: string, data: {
        reason?: string
        forceMajeure?: boolean
    }) {
        return this.request(`/api/proposals/${id}/cancel`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    // Public proposal
    async getPublicProposal(publicId: string) {
        return this.request(`/api/public/${publicId}`)
    }

    async acceptTerms(publicId: string, data: {
        ip: string
        userAgent: string
        termsVersion: string
    }) {
        return this.request(`/api/public/${publicId}/accept-terms`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async createCheckout(publicId: string, data: {
        paymentProvider: 'stripe' | 'asaas'
    }) {
        return this.request(`/api/public/${publicId}/checkout`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    // Payments
    async createBalanceLink(
        proposalId: string,
        provider: 'stripe' | 'asaas' = 'stripe'
    ) {
        return this.request<{ checkoutUrl: string; providerPaymentId: string }>(
            `/api/payments/${proposalId}/balance-link`,
            {
                method: 'POST',
                body: JSON.stringify({ provider }),
            }
        )
    }

    async reviewManualPix(paymentId: string, action: 'confirm' | 'reject') {
        return this.request<{ status: string }>(
            `/api/payments/${paymentId}/manual-pix/review`,
            {
                method: 'POST',
                body: JSON.stringify({ action }),
            }
        )
    }

    // Client error logging (server-side alerting)
    async logClientSubscriptionError(payload: {
        type: string
        message: string
        retryable: boolean
        severity?: 'low' | 'medium' | 'high'
        originalError?: string
        context?: Record<string, unknown>
    }) {
        return this.request<{ ok: true; errorId: string }>(`/api/client-errors`, {
            method: 'POST',
            body: JSON.stringify({
                category: 'subscription',
                ...payload,
            }),
        })
    }

    // Onboarding
    async getOnboardingStatus() {
        return this.request<{
            flow: 'studio' | 'professional'
            role: string
            tenantName: string | null
            hasCompletedOnboarding: boolean
            onboardingStep: number
            onboardingCompletedAt: string | null
            integrations: {
                stripe: boolean
                calendar: boolean
            }
            availability: {
                configured: boolean
                timeZone: string | null
                activeWindowsCount: number
            }
            hasPackages: boolean
            hasContractTemplate: boolean
            professional: {
                hasProfile: boolean
                displayName: string | null
                publicationStatus: string | null
                assignmentsCount: number
                availabilityWindowsCount: number
            } | null
        }>('/api/onboarding/status')
    }

    async updateOnboardingStep(step: number) {
        return this.request<{
            onboardingStep: number
            hasCompletedOnboarding: boolean
        }>('/api/onboarding/step', {
            method: 'PATCH',
            body: JSON.stringify({ step }),
        })
    }

    async completeOnboarding(completed = true) {
        return this.request<{
            hasCompletedOnboarding: boolean
            onboardingStep: number
            onboardingCompletedAt: string | null
        }>('/api/onboarding/complete', {
            method: 'POST',
            body: JSON.stringify({ completed }),
        })
    }

    async resetOnboarding() {
        return this.request<{
            hasCompletedOnboarding: boolean
            onboardingStep: number
            onboardingCompletedAt: string | null
        }>('/api/onboarding/reset', {
            method: 'POST',
        })
    }

    async getAvailabilityWindows() {
        return this.request<Array<{
            id: string
            weekday: number
            startMinutes: number
            endMinutes: number
            label?: string | null
            active?: boolean
        }>>('/api/availability/windows')
    }

    async saveOnboardingAvailability(payload: {
        timeZone: string
        windows: Array<{
            weekday: number
            startMinutes: number
            endMinutes: number
            label?: string | null
            active?: boolean
        }>
        importGoogle?: {
            enabled: boolean
            fromDate: string
            toDate: string
            mergeMode: 'append_only'
        }
    }) {
        return this.request<{
            availability: {
                configured: boolean
                timeZone: string
                activeWindowsCount: number
            }
            googleImport: {
                requested: boolean
                importedExceptionsCount: number
                skippedExceptionsCount: number
                fromDate: string | null
                toDate: string | null
            }
        }>('/api/onboarding/availability', {
            method: 'POST',
            body: JSON.stringify(payload),
        })
    }
    // ── Seats / Team Management ──────────────────────────────────

    async getSeatDashboard() {
        return this.request<{
            seats: {
                included: number
                extra: number
                total: number
                occupied: number
                available: number
            }
            statusCounts: Record<string, number>
            pendingInvites: number
            alerts: Array<{ type: string; message: string; severity: string }>
            security: {
                require2FA: boolean
                membersWithout2FA: number
            }
        }>('/api/seats/dashboard')
    }

    async getSeatMembers(params?: {
        search?: string
        role?: string
        status?: string
        page?: number
        limit?: number
    }) {
        const query = new URLSearchParams()
        if (params?.search) query.set('search', params.search)
        if (params?.role) query.set('role', params.role)
        if (params?.status) query.set('status', params.status)
        if (params?.page) query.set('page', String(params.page))
        if (params?.limit) query.set('limit', String(params.limit))
        const qs = query.toString()
        return this.request<{
            items: Array<{
                id: string
                name: string
                email: string
                role: string
                seatStatus: string
                lastAccessAt: string | null
                createdAt: string
                twoFactorEnabled: boolean
                metrics: {
                    proposalsSent: number
                    conversionRate: number
                    revenueCents: number
                    paymentsCount: number
                }
            }>
            total: number
            page: number
            limit: number
        }>(`/api/seats/members${qs ? `?${qs}` : ''}`)
    }

    async getSeatMemberDetail(userId: string) {
        return this.request(`/api/seats/members/${userId}`)
    }

    async changeMemberRole(userId: string, data: { role: string; resetPermissionsToDefaults?: boolean }) {
        return this.request(`/api/seats/members/${userId}/role`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        })
    }

    async updateMemberPermissions(userId: string, overrides: Record<string, boolean>) {
        return this.request(`/api/seats/members/${userId}/permissions`, {
            method: 'PATCH',
            body: JSON.stringify({ overrides }),
        })
    }

    async suspendMember(userId: string, reason?: string) {
        return this.request(`/api/seats/members/${userId}/suspend`, {
            method: 'POST',
            body: JSON.stringify({ reason }),
        })
    }

    async unsuspendMember(userId: string) {
        return this.request(`/api/seats/members/${userId}/unsuspend`, {
            method: 'POST',
        })
    }

    async removeMember(userId: string, data?: {
        transferToUserId?: string
        archiveProposals?: boolean
        transferPackages?: boolean
    }) {
        return this.request(`/api/seats/members/${userId}/remove`, {
            method: 'POST',
            body: JSON.stringify(data ?? {}),
        })
    }

    async transferMemberOwnership(userId: string, data: {
        targetUserId: string
        proposalIds?: string[]
        packageIds?: string[]
    }) {
        return this.request(`/api/seats/members/${userId}/transfer`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async createInvite(data: {
        email?: string
        role: string
        customMessage?: string
        expirationDays?: number
    }) {
        return this.request<{
            inviteId: string
            publicToken: string
            shareableLink: string
            email: string | null
            role: string
            expiresAt: string
        }>('/api/seats/invite', {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    async getInvites() {
        return this.request<Array<{
            id: string
            email: string | null
            role: string
            status: string
            publicToken: string
            expiresAt: string
            createdAt: string
        }>>('/api/seats/invites')
    }

    async revokeInvite(id: string) {
        return this.request(`/api/seats/invites/${id}/revoke`, { method: 'POST' })
    }

    async resendInvite(id: string) {
        return this.request(`/api/seats/invites/${id}/resend`, { method: 'POST' })
    }

    async getInviteDetails(token: string) {
        return this.request<{
            valid: boolean
            tenantName?: string
            role?: string
            email?: string
            customMessage?: string
            expiresAt?: string
            error?: string
        }>(`/api/seats/invite/${token}`)
    }

    async acceptInvite(token: string, data: { name: string; email: string; password: string }) {
        return this.request<{
            userId: string
            tenantId: string
            tenantName: string
            tenantSlug: string
            role: string
            mode: 'created' | 'joined_existing'
        }>(`/api/seats/accept-invite/${token}`, {
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    // Accept an invite using the caller's existing (already authenticated) account.
    async acceptInviteAuthenticated(token: string) {
        return this.request<{
            userId: string
            tenantId: string
            tenantName: string
            tenantSlug: string
            role: string
            mode: 'created' | 'joined_existing'
        }>(`/api/seats/accept-invite/${token}/join`, {
            method: 'POST',
        })
    }

    async getMemberships() {
        return this.request<Membership[]>('/api/memberships')
    }

    async switchTenant(tenantId: string): Promise<LoginResult> {
        const result = await this.request<LoginResult>('/api/memberships/switch', {
            method: 'POST',
            body: JSON.stringify({ tenantId }),
        })

        // Switching into a tenant that requires 2FA may return a challenge/enrollment
        // step instead of a session — only persist a token when actually authenticated.
        if (result.status === 'authenticated') {
            this.setAuthToken(result.token)
            this.refreshCsrfToken()
        }
        return result
    }

    async getTenantRoles() {
        return this.request<Array<{
            id: string
            role: string
            [key: string]: unknown
        }>>('/api/seats/roles')
    }

    async updateTenantRole(role: string, permissions: Record<string, boolean>) {
        return this.request(`/api/seats/roles/${role}`, {
            method: 'PUT',
            body: JSON.stringify(permissions),
        })
    }

    async getSeatAuditLog(params?: {
        action?: string
        userId?: string
        dateFrom?: string
        dateTo?: string
        page?: number
        limit?: number
    }) {
        const query = new URLSearchParams()
        if (params?.action) query.set('action', params.action)
        if (params?.userId) query.set('userId', params.userId)
        if (params?.dateFrom) query.set('dateFrom', params.dateFrom)
        if (params?.dateTo) query.set('dateTo', params.dateTo)
        if (params?.page) query.set('page', String(params.page))
        if (params?.limit) query.set('limit', String(params.limit))
        const qs = query.toString()
        return this.request<{
            items: Array<{
                id: string
                action: string
                userId: string | null
                entityType: string
                entityId: string | null
                newData: unknown
                previousData: unknown
                createdAt: string
            }>
            total: number
            page: number
            limit: number
        }>(`/api/seats/audit-log${qs ? `?${qs}` : ''}`)
    }

    async getSeatReports(params?: { period?: string; userId?: string }) {
        const query = new URLSearchParams()
        if (params?.period) query.set('period', params.period)
        if (params?.userId) query.set('userId', params.userId)
        const qs = query.toString()
        return this.request<{
            period: string
            dateFrom: string
            members: Array<{
                userId: string
                name: string
                email: string
                role: string
                proposalsSent: number
                accepted: number
                expired: number
                cancelled: number
                conversionRate: number
                noShowRate: number
                revenueCents: number
                paymentsCount: number
            }>
            totals: {
                proposalsSent: number
                accepted: number
                revenueCents: number
                paymentsCount: number
            }
        }>(`/api/seats/reports${qs ? `?${qs}` : ''}`)
    }

    async exportSeatReports(params?: { period?: string; userId?: string }) {
        const query = new URLSearchParams()
        if (params?.period) query.set('period', params.period)
        if (params?.userId) query.set('userId', params.userId)
        query.set('format', 'csv')
        return this.request<string>(`/api/seats/reports/export?${query.toString()}`)
    }

    async getSeatBillingSummary() {
        return this.request<SeatBillingSummary>('/api/seats/billing/summary')
    }

    async previewSeatChange(addQuantity: number, interval?: 'month' | 'year') {
        return this.request<SeatChangePreview>('/api/seats/billing/preview', {
            method: 'POST',
            body: JSON.stringify({ addQuantity, interval }),
        })
    }

    async purchaseSeats(addQuantity: number, options: { interval?: 'month' | 'year'; idempotencyKey: string }) {
        return this.request<SeatBillingSummary & { chargedNowCents: number }>('/api/seats/billing/purchase', {
            method: 'POST',
            body: JSON.stringify({
                addQuantity,
                interval: options.interval,
                idempotencyKey: options.idempotencyKey,
            }),
        })
    }

    async scheduleSeatRemoval(removeQuantity: number) {
        return this.request<{ effectiveAt: string; newExtraQuantity: number; maxRemovable: number }>(
            '/api/seats/billing/schedule-removal',
            {
                method: 'POST',
                body: JSON.stringify({ removeQuantity }),
            },
        )
    }

    async cancelScheduledSeatRemoval() {
        return this.request<SeatBillingSummary>('/api/seats/billing/cancel-scheduled-removal', {
            method: 'POST',
        })
    }

    async getSeatBillingHistory() {
        return this.request<DomainSeatEvent[]>('/api/seats/billing/history')
    }

    async startExtraSeatCheckout(addQuantity: number, interval?: 'month' | 'year') {
        return this.request<{ url: string }>('/api/seats/billing/checkout', {
            method: 'POST',
            body: JSON.stringify({ addQuantity, interval }),
        })
    }

    async getSeatSessions() {
        return this.request<Array<{
            id: string
            userId: string
            userName: string
            userEmail: string
            ipAddress: string | null
            userAgent: string | null
            deviceInfo: string | null
            lastActiveAt: string
            createdAt: string
            expiresAt: string
        }>>('/api/seats/sessions')
    }

    async terminateSeatSession(sessionId: string) {
        return this.request(`/api/seats/sessions/${sessionId}`, { method: 'DELETE' })
    }

    async updateSecuritySettings(data: { require2FA?: boolean }) {
        return this.request('/api/seats/security', {
            method: 'PATCH',
            body: JSON.stringify(data),
        })
    }

    // ── Professionals ────────────────────────────────────────────

    async getProfessionals() {
        return this.request<{ professionals: StudioProfessionalListItem[] }>('/api/professionals')
    }

    async getMyProfessional() {
        return this.request<{ professional: MyProfessionalRecord | null }>('/api/professionals/me')
    }

    async respondProfessionalConsent(action: 'accept' | 'decline') {
        return this.request<ProfessionalProfileSummary>('/api/professionals/consent', {
            method: 'POST',
            body: JSON.stringify({ action }),
        })
    }

    async getPackageAssignments(packageId: string) {
        return this.request<{ assignments: PackageServiceAssignment[] }>(
            `/api/professionals/packages/${packageId}/assignments`
        )
    }

    async setPackageAssignments(packageId: string, assignments: ServiceAssignmentInput[]) {
        return this.request<{ assignments: PackageServiceAssignment[] }>(
            `/api/professionals/packages/${packageId}/assignments`,
            {
                method: 'PUT',
                body: JSON.stringify({ assignments }),
            }
        )
    }

    async upsertProfessionalProfile(userId: string, data: UpsertProfessionalProfileInput) {
        return this.request<ProfessionalProfileSummary>(`/api/professionals/${userId}/profile`, {
            method: 'PUT',
            body: JSON.stringify(data),
        })
    }

    async requestProfilePublication(userId: string) {
        return this.request<ProfessionalProfileSummary>(
            `/api/professionals/${userId}/publication-request`,
            { method: 'POST' }
        )
    }

    async unpublishProfessionalProfile(userId: string) {
        return this.request<ProfessionalProfileSummary>(`/api/professionals/${userId}/unpublish`, {
            method: 'POST',
        })
    }

    async addProfessionalPortfolioItem(
        userId: string,
        data: { imageUrl: string; imagePublicId: string; caption?: string | null }
    ) {
        return this.request<{ portfolioItems: ProfessionalPortfolioItem[] }>(
            `/api/professionals/${userId}/portfolio`,
            {
                method: 'POST',
                body: JSON.stringify(data),
            }
        )
    }

    async removeProfessionalPortfolioItem(userId: string, itemId: string) {
        return this.request<{ portfolioItems: ProfessionalPortfolioItem[] }>(
            `/api/professionals/${userId}/portfolio/${itemId}`,
            { method: 'DELETE' }
        )
    }

    async reorderProfessionalPortfolio(userId: string, orderedItemIds: string[]) {
        return this.request<{ portfolioItems: ProfessionalPortfolioItem[] }>(
            `/api/professionals/${userId}/portfolio/order`,
            {
                method: 'PUT',
                body: JSON.stringify({ orderedItemIds }),
            }
        )
    }

    // ── Availability ─────────────────────────────────────────────

    async getProfessionalAvailability(userId: string) {
        return this.request<ProfessionalAvailability>(
            `/api/professionals/${userId}/availability`,
        )
    }

    async setProfessionalAvailabilityWindows(userId: string, windows: AvailabilityWindowInput[]) {
        return this.request<ProfessionalAvailability>(
            `/api/professionals/${userId}/availability/windows`,
            {
                method: 'PUT',
                body: JSON.stringify({ windows }),
            }
        )
    }

    async createProfessionalAvailabilityException(userId: string, data: AvailabilityExceptionInput) {
        return this.request<AvailabilityException>(
            `/api/professionals/${userId}/availability/exceptions`,
            {
                method: 'POST',
                body: JSON.stringify(data),
            }
        )
    }

    async updateProfessionalAvailabilityException(
        userId: string,
        exceptionId: string,
        data: Partial<AvailabilityExceptionInput>,
    ) {
        return this.request<AvailabilityException>(
            `/api/professionals/${userId}/availability/exceptions/${exceptionId}`,
            {
                method: 'PUT',
                body: JSON.stringify(data),
            }
        )
    }

    async deleteProfessionalAvailabilityException(userId: string, exceptionId: string) {
        return this.request(
            `/api/professionals/${userId}/availability/exceptions/${exceptionId}`,
            { method: 'DELETE' }
        )
    }
}

// Export singleton instance
export const api = new ApiClient()
